import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';
import { timeGuard } from '@confluence/query-preloader-tools/entry-points/timeGuard';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs';

import { fetchORSForSSR } from '../entry-points/fetchORSForSSR';
import { getInitialState } from '../entry-points/getInitialState';
import { getSmartLinks } from '../entry-points/getSmartLinks';

import { ContentSmartLinksQuery } from './ContentSmartLinksQuery.graphql';
import { ContentSmartLinksQueryForDraft } from './ContentSmartLinksQueryForDraft.graphql';
import type {
	ContentSmartLinksQuery as ContentSmartLinksQueryType,
	ContentSmartLinksQueryVariables as ContentSmartLinksQueryVariablesType,
} from './__types__/ContentSmartLinksQuery';
import type {
	ContentSmartLinksQueryForDraft as ContentSmartLinksQueryForDraftType,
	ContentSmartLinksQueryVariablesForDraft as ContentSmartLinksQueryVariablesForDraftType,
} from './__types__/ContentSmartLinksQueryForDraft';
import {
	groupSmartLinksByHostname,
	isFirstParty,
	mergeSmallThirdPartyBatches,
	splitLinksIntoBatches,
} from './smartLinkBatchHelpers';
import type { SmartLinkType } from './getSmartLinks';

declare global {
	interface Window {
		__SSR_SMARTLINKS__?: { [key: string]: any } | {};
		__SSR_SMARTLINKS_STATS__?: {
			fetch: number;
			fetchTimeout: number;
			total: number;
			resolved: number;
			timeout: number;
			firstParty: { total: number; resolved: number };
			thirdParty: { total: number; resolved: number };
		};
	}
}

const SMART_LINK_INDIVIDUAL_TIMEOUT = 250;
const SMART_LINK_BATCH_TIMEOUT = 350;
const DEFAULT_MIN_BATCH_COUNT = 1;
const DEFAULT_MAX_BATCH_COUNT = 50;

const preloadContentSmartlinksQuery = async (
	contentId: string,
	isDraft: boolean = false,
): Promise<SmartLinkType[]> => {
	if (isDraft) {
		const smartLinkDraftRes = await query<
			ContentSmartLinksQueryForDraftType,
			ContentSmartLinksQueryVariablesForDraftType
		>({
			/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
			query: ContentSmartLinksQueryForDraft,
			variables: {
				contentId,
			},
		});
		return getSmartLinks(smartLinkDraftRes, isDraft);
	}

	const smartLinksRes = await query<
		ContentSmartLinksQueryType,
		ContentSmartLinksQueryVariablesType
	>({
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		query: ContentSmartLinksQuery,
		variables: {
			contentId,
		},
	});
	return getSmartLinks(smartLinksRes, isDraft);
};

export const getSmartLinksStats = (
	orsCalls: any[],
	smartLinks: any[],
	resolvedSmartLinks: any[],
	fetchStats: {
		fetchTimeout: number;
	},
) => {
	const totalLinks = Array.from(new Set<string>(smartLinks.map(({ resourceUrl }) => resourceUrl)));
	let timeout = 0;
	const resolvedLinks = new Set(
		resolvedSmartLinks
			.filter(({ status }) => {
				if (status === 504) {
					timeout++;
				}
				return status === 200;
			})
			.map(({ body }) => body?.data?.url)
			.filter(Boolean),
	);

	return {
		fetch: orsCalls.length,
		fetchTimeout: fetchStats.fetchTimeout,
		total: totalLinks.length,
		resolved: resolvedLinks.size,
		timeout,
		...totalLinks.reduce(
			(acc, link: string) => {
				// It's just a heuristic we can't really know if the URL is first party without actually resolving it.
				if (isFirstParty(link)) {
					acc.firstParty.total++;
					if (resolvedLinks.has(link)) {
						acc.firstParty.resolved++;
					}
				} else {
					acc.thirdParty.total++;
					if (resolvedLinks.has(link)) {
						acc.thirdParty.resolved++;
					}
				}
				return acc;
			},
			{
				firstParty: { total: 0, resolved: 0 },
				thirdParty: { total: 0, resolved: 0 },
			},
		),
	};
};

async function fetchORSForSSRWithTimeout(
	links: SmartLinkType[],
	timeout: number,
	batchTimeout: number,
	stats: {
		fetchTimeout: number;
	},
) {
	const { customURLs, userContext } = getSSRRenderInputs();
	const objectResolverURL = customURLs?.['object-resolver-service']!;

	return timeGuard(
		async () => {
			try {
				const res = await fetchORSForSSR({
					// Despite the API has a timeout it does not respect it. It can easily go over time.
					url: `${objectResolverURL}?timeout=${timeout}`,
					body: JSON.stringify(links),
					userContext,
				});
				if (!res.ok) {
					throw new Error(`${res.status} ${res.statusText}`);
				}
				return res.json();
			} catch (e) {
				ssrPrepareErrorsClient.submitError(
					new Error(`Smart link content ORS call failed: ${e.message}`),
				);
			}
			return [];
		},
		() => {
			stats.fetchTimeout++;
			return [];
		},
		batchTimeout,
	);
}

/*
 * preloadContentSmartLinks tracks the entire preloaded chain of queries needed to SSR Content Smart Links. This consists of two calls:
 * 1. ContentSmartLinksQuery, a GQL call to fetch SmartLinks URLs and
 * 2. A call to ORS to resolve the metadata, which uses the links returned from the previous call
 * The entirety of this chain is currently best effort, which means that SmartLinks will either resolve ALL
 * links by the time the longest preloaded query resolves or NONE.
 *
 * If a timeout is provided, Smartlinks will try and resolve as many links as possible
 * within the provided timeout.
 */

export const preloadContentSmartLinks = async (contentId: string, isDraft: boolean = false) => {
	const smartLinks = await preloadContentSmartlinksQuery(contentId, isDraft);
	if (!smartLinks?.length) {
		return;
	}

	// How long a single ORS resolution can take
	const timeout = StatsigConfigurations.getNumber(
		'confluence_ssr_content_smart_link_max_time',
		SMART_LINK_INDIVIDUAL_TIMEOUT,
	);

	// How long a batch of ORS resolution can take
	const batchTimeout = StatsigConfigurations.getNumber(
		'confluence_ssr_content_smart_link_batch_max_time',
		SMART_LINK_BATCH_TIMEOUT,
	);

	// How many links should be packed in a batch
	const min = StatsigConfigurations.getNumber(
		'confluence_ssr_content_smart_link_min_batch_count',
		DEFAULT_MIN_BATCH_COUNT,
	);
	const max = Math.max(
		min,
		StatsigConfigurations.getNumber(
			'confluence_ssr_content_smart_link_max_batch_count',
			DEFAULT_MAX_BATCH_COUNT,
		),
	);

	await SSRMeasures.run('ssr-app/prepare/preloadQuery/fetch:contentSmartLinksORS', async () => {
		const orsCalls: Promise<any>[] = [];
		const fetchStats = {
			fetchTimeout: 0,
		};
		let smartLinksBatches = groupSmartLinksByHostname(smartLinks);
		smartLinksBatches = mergeSmallThirdPartyBatches(smartLinksBatches, min);
		smartLinksBatches = splitLinksIntoBatches(smartLinksBatches, max);

		for (const smartLinksBatch of smartLinksBatches) {
			orsCalls.push(fetchORSForSSRWithTimeout(smartLinksBatch, timeout, batchTimeout, fetchStats));
		}

		// eslint-disable-next-line check-preloaders/no-promise-all
		const resolvedSmartLinks = (await Promise.all(orsCalls)).flat();

		// Format response to fit SmartCard Provider storeOptions initial state
		// Save correctly formatted response to window obj so it can be used on SSR and SPA (no duplicate calls)
		window['__SSR_SMARTLINKS__'] = getInitialState(resolvedSmartLinks);
		window['__SSR_SMARTLINKS_STATS__'] = getSmartLinksStats(
			orsCalls,
			smartLinks,
			resolvedSmartLinks,
			fetchStats,
		);
	});
};
